import React from "react";
import { Link, useLocation } from "react-router-dom";
import { HandHeart, MapPin, Phone, Mail, ShieldCheck, Facebook, Instagram, Youtube, Linkedin, Heart } from "lucide-react";
import { NAV_LINKS, CONTACT } from "../lib/content";
import { fetchSiteSettings } from "../lib/api";
import { useApiData } from "../hooks/useApiData";

const mapContactSettings = (settings) => ({
  address: settings.address || CONTACT.address,
  person: settings.contact_person || CONTACT.person,
  phone: settings.phone || CONTACT.phone,
  email: settings.email || CONTACT.email,
  facebook: settings.facebook_url || "#",
  instagram: settings.instagram_url || "#",
  linkedin: settings.linkedin_url || "#",
  youtube: settings.youtube_url || "#",
});

const SOCIAL_LINKS = [
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin },
  { key: "youtube", label: "YouTube", Icon: Youtube },
];

const TRUST_POINTS = [
  "Registered NGO since 2014 · Reg. No. 439/2014",
  "12A & 80G certified — your donation is tax-exempt",
  "CSR-1 registered · Eligible for corporate CSR funding",
];

export const Footer = () => {
  const { pathname } = useLocation();
  const isContactPage = pathname === "/contact";
  const contact = useApiData(fetchSiteSettings, CONTACT, mapContactSettings);

  return (
  <footer className="relative bg-navy text-white overflow-hidden" data-testid="site-footer">
    <div className="absolute inset-0 grain opacity-[0.06] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 lg:py-20 relative">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-11 w-11 rounded-2xl bg-teal-600 text-white">
              <HandHeart className="h-6 w-6" />
            </span>
            <span className="font-display font-extrabold text-xl">Janma Foundation</span>
          </div>
          <p className="mt-5 text-white/70 leading-relaxed max-w-sm">
            Since 2014, healing hearts with helping hands — rooted across Telangana & Andhra
            Pradesh and reaching communities throughout India — through education, health, skill
            development and women empowerment.
          </p>
          <div className="mt-6 flex items-center gap-3">
            {SOCIAL_LINKS.map(({ key, label, Icon }) => (
              <a
                key={key}
                href={contact[key]}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`footer-social-${key}`}
                className="grid place-items-center h-10 w-10 rounded-full bg-white/10 hover:bg-coral transition-colors"
              >
                <Icon className="h-4.5 w-4.5" />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-teal-200">Quick Links</h4>
          <ul className="mt-5 space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-teal-200">
            {isContactPage ? "Reach Us" : "Registered & Trusted"}
          </h4>
          {isContactPage ? (
            <ul className="mt-5 space-y-4 text-white/70">
              <li className="flex gap-3">
                <MapPin className="h-5 w-5 text-coral shrink-0 mt-0.5" />
                <span>{contact.address}</span>
              </li>
              <li className="flex gap-3 items-center">
                <Phone className="h-5 w-5 text-coral shrink-0" />
                <a href={`tel:${contact.phone}`} className="hover:text-white">
                  {contact.person} — {contact.phone}
                </a>
              </li>
              <li className="flex gap-3 items-center">
                <Mail className="h-5 w-5 text-coral shrink-0" />
                <a href={`mailto:${contact.email}`} className="hover:text-white">
                  {contact.email}
                </a>
              </li>
            </ul>
          ) : (
            <ul className="mt-5 space-y-4 text-white/70">
              {TRUST_POINTS.map((point) => (
                <li key={point} className="flex gap-3">
                  <ShieldCheck className="h-5 w-5 text-coral shrink-0 mt-0.5" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          )}
          <Link
            to="/get-involved#donate"
            data-testid="footer-donate-button"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-coral px-5 py-3 text-sm font-semibold text-white hover:bg-coral-700 transition-colors"
          >
            <Heart className="h-4 w-4 fill-white" /> Donate & Get 80G Benefit
          </Link>
        </div>
      </div>

      <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/50">
        <p>© {new Date().getFullYear()} Janma Foundation. All rights reserved.</p>
        <p className="font-mono text-xs">Reg. 439/2014 · 12A · 80G · CSR-1 · PAN AADAJ4366N</p>
      </div>
    </div>
  </footer>
  );
};
